import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DefaultStoreProjectAssigneeCreateForm } from "@/types/defaultStoreProjectAssignee";
import PageHeader from "@/components/PageHeader";
import { useDefaultStoreProjectAssigneeBreadcrumbs } from "@/modules/defaultStoreProjectAssignees/hooks/useDefaultStoreProjectAssigneeBreadcrumbs";
import {
  defaultStoreProjectAssigneeCreateForm,
  defaultStoreProjectAssigneeCreate,
} from "@/schemas/defaultStoreProjectAssignee";
import { useCallback, useMemo } from "react";
import Form from "@/components/Form";
import PageContent from "@/components/PageContent";
import { DefaultStoreProjectAssigneesCreatorSaveButton } from "./DefaultStoreProjectAssigneesCreatorSaveButton";
import { DefaultStoreProjectAssigneesCreatorProjectTypes } from "./DefaultStoreProjectAssigneesCreatorProjectTypes";
import { DefaultStoreProjectAssigneesCreatorStores } from "./DefaultStoreProjectAssigneesCreatorStores";
import { Tabs, TabsTrigger, TabsList, TabsContent } from "@/components/ui/tabs";
import { isRequired } from "@/lib/form";
import CheckboxField from "@/components/CheckboxField";
import UserSelect from "@/components/UserSelect";
import Field from "@/components/Field";
import { useCreateDefaultStoreProjectAssignee } from "@/modules/defaultStoreProjectAssignees/hooks/useCreateDefaultStoreProjectAssignee";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CheckedState } from "@radix-ui/react-checkbox";
import { SystemRoleCodesType } from "@/types/user";
import { SUPPORTED_SYSTEM_ROLE_CODES } from "../constants";
import { useCreateRowSelectionStore } from "@/components/data-table/hooks/useCreateRowSelectionStore";
import { RowSelectionCountBadge } from "@/components/data-table/components/RowSelectionCountBadge";
import { isEmptyArray } from "@/lib/utils/isEmptyArray";

const defaultValues: DefaultValues<DefaultStoreProjectAssigneeCreateForm> = {
  is_primary: false,
  user_id: "",
  selectedSystemRoleCodes: SUPPORTED_SYSTEM_ROLE_CODES,
};

export default function DefaultStoreProjectAssigneesCreator() {
  const { t } = useTranslation();
  const projectTypesStore = useCreateRowSelectionStore();
  const storesStore = useCreateRowSelectionStore();

  const breadcrumbItems = useDefaultStoreProjectAssigneeBreadcrumbs([
    {
      label: t("defaultStoreProjectAssignees.title.add"),
    },
  ]);
  const { can_create } = useModulePermissions();
  const mutation = useCreateDefaultStoreProjectAssignee();

  const isDisabled = mutation.isPending || !can_create;

  const form = useForm<DefaultStoreProjectAssigneeCreateForm>({
    resolver: zodResolver(defaultStoreProjectAssigneeCreateForm),
    defaultValues,
    disabled: isDisabled,
  });

  useFormUnsavedChangesWarning(form);

  const selectedSystemRoleCodes = form.watch("selectedSystemRoleCodes");

  const handleSubmit = useCallback(
    async (data: DefaultStoreProjectAssigneeCreateForm) => {
      const payload = defaultStoreProjectAssigneeCreate.parse({
        ...data,
        store_ids: Array.from(storesStore.getSelection()),
        project_type_ids: Array.from(projectTypesStore.getSelection()),
      });
      await mutation.mutateAsync(payload);
    },
    [mutation, storesStore, projectTypesStore],
  );

  const supportedRoles = useMemo(
    () =>
      SUPPORTED_SYSTEM_ROLE_CODES.map((systemRoleCode) => ({
        value: systemRoleCode,
        label: t(`userRole.${systemRoleCode}`),
      })),
    [t],
  );

  const handleRoleChange = useCallback(
    (role: SystemRoleCodesType, checked: CheckedState) => {
      const current = form.getValues(
        "selectedSystemRoleCodes",
      ) as SystemRoleCodesType[];
      let updated: SystemRoleCodesType[];
      if (checked) {
        updated = current.includes(role) ? current : [...current, role];
      } else {
        updated = current.filter((r) => r !== role);
      }
      form.setValue("selectedSystemRoleCodes", updated, {
        shouldValidate: false,
      });
    },
    [form],
  );

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <Form form={form} handleSubmit={handleSubmit}>
        <PageContent
          title={t("defaultStoreProjectAssignees.title.add")}
          footer={
            <DefaultStoreProjectAssigneesCreatorSaveButton
              projectTypesStore={projectTypesStore}
              storesStore={storesStore}
            />
          }
        >
          <div className="flex flex-row gap-4 mt-6">
            <div className="text-sm">
              {t("defaultStoreProjectAssignees.title.role")}
            </div>
            {supportedRoles.map((role) => (
              <Label
                className="flex items-center gap-2 font-normal"
                key={role.value}
              >
                <Checkbox
                  value={role.value}
                  onCheckedChange={(checked) =>
                    handleRoleChange(role.value, checked)
                  }
                  checked={
                    selectedSystemRoleCodes?.includes(role.value) ?? false
                  }
                />
                {role.label}
              </Label>
            ))}
          </div>

          <div className="flex flex-row gap-4 my-6">
            <Field
              name="user_id"
              label={t("defaultStoreProjectAssignees.fields.user_name")}
              required={isRequired(
                defaultStoreProjectAssigneeCreateForm.shape.user_id,
              )}
              render={({ field }) => (
                <UserSelect
                  {...field}
                  className="w-80"
                  loading="eager"
                  loadingCondition={!isEmptyArray(selectedSystemRoleCodes)}
                  systemRoleCodes={selectedSystemRoleCodes ?? []}
                  showRole={true}
                />
              )}
            />
            <CheckboxField
              name="is_primary"
              label={t(`defaultStoreProjectAssignees.fields.is_primary`)}
              required={isRequired(
                defaultStoreProjectAssigneeCreate.shape.is_primary,
              )}
            />
          </div>

          <Tabs defaultValue="project_types">
            <TabsList>
              <TabsTrigger value="project_types" className="gap-2">
                {t("defaultStoreProjectAssignees.title.project_types")}
                <RowSelectionCountBadge store={projectTypesStore} />
              </TabsTrigger>
              <TabsTrigger value="stores" className="gap-2">
                {t("defaultStoreProjectAssignees.title.stores")}
                <RowSelectionCountBadge store={storesStore} />
              </TabsTrigger>
            </TabsList>
            <TabsContent value="project_types">
              <DefaultStoreProjectAssigneesCreatorProjectTypes
                store={projectTypesStore}
              />
            </TabsContent>
            <TabsContent value="stores">
              <DefaultStoreProjectAssigneesCreatorStores store={storesStore} />
            </TabsContent>
          </Tabs>
        </PageContent>
      </Form>
    </>
  );
}
